import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Loader2, Search, Sparkles } from "lucide-react";
import { api, LOGO } from "@/lib/api";
import { Input } from "@/components/ui/input";

const eur = (n) => `€ ${Number(n || 0).toFixed(2).replace(".", ",")}`;
const WA = "M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.2-.5-2.4-1.5-.9-.8-1.5-1.8-1.6-2.1-.2-.3 0-.5.1-.6l.4-.5c.2-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5 2.5 1 3 .8 3.5.7.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3zM12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2z";

const Share = ({ url, text, testid }) => (
  <div className="flex gap-1.5 shrink-0">
    <button onClick={() => navigator.clipboard.writeText(url).then(() => toast.success("Link copiato"))} data-testid={`${testid}-copy`}
      className="inline-flex items-center gap-1 rounded-full border border-[#E5DED7] px-3 py-1.5 text-xs text-[#5C534E] hover:bg-[#F4EFEA] transition-colors">
      <Copy size={12} /> Copia link
    </button>
    <a href={`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`} target="_blank" rel="noreferrer" data-testid={`${testid}-wa`}
      aria-label="Condividi su WhatsApp" className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#25D366] text-white hover:brightness-95">
      <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d={WA} /></svg>
    </a>
  </div>
);

const Stats = ({ d }) => {
  const items = [["Visite al link", d.clicks], ["Mamme iscritte", d.customers], ["Ordini", d.orders], ["Venduto", eur(d.sales)],
    ["Guadagno totale", eur(d.commission)], ["Già ricevuto", eur(d.paid)], ["Da ricevere", eur(d.to_pay)]];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="portal-stats">
      {items.map(([l, v], i) => (
        <div key={l} className={`rounded-2xl p-4 ${i === 6 ? "bg-[#F9ECE9]" : "bg-white border border-[#EAE3DC]"}`}>
          <div className="text-[11px] uppercase tracking-wider text-[#8C7F78]">{l}</div>
          <div className={`mt-1 font-serif text-2xl ${i === 6 ? "text-[#C87D75]" : "text-[#2D2825]"}`} data-testid={`portal-stat-${i}`}>{v}</div>
        </div>
      ))}
    </div>
  );
};

const Orders = ({ recent }) => (
  <div className="rounded-2xl bg-white border border-[#EAE3DC] p-5" data-testid="portal-orders">
    <h2 className="font-medium text-[#2D2825]">Ultimi ordini</h2>
    {recent.length === 0 ? <p className="text-sm text-[#8C7F78] mt-2">Ancora nessun ordine: condividi il tuo link per iniziare.</p> : (
      <div className="mt-3 divide-y divide-[#F1ECE7] text-sm">
        {recent.map((o) => (
          <div key={o.name} className="py-2 flex justify-between gap-2 text-[#5C534E]">
            <span>{o.date.split("-").reverse().join("/")}</span>
            <span>{eur(o.subtotal)} → <strong className="text-[#2D2825]">{eur(o.commission)}</strong>{o.paid ? " · ricevuto" : ""}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const ProductRow = ({ p, code }) => (
  <div className="flex items-start sm:items-center gap-3 py-2.5" data-testid={`portal-product-${p.id}`}>
    <img src={p.image} alt="" className="w-14 h-14 rounded-xl object-cover bg-[#F4EFEA] shrink-0" />
    <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-2">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[#2D2825] line-clamp-2">{p.title}</div>
        <div className="text-xs text-[#8C7F78]">{p.price ? eur(p.price) : ""}</div>
      </div>
      <Share url={`${window.location.origin}/prodotto/${p.id}?ref=${code}`} text={`Guarda questo capo Minime Baby: ${p.title}`} testid={`portal-product-${p.id}`} />
    </div>
  </div>
);

const Products = ({ code }) => {
  const [all, setAll] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/catalog", { params: { available: true } }).then((r) => setAll(r.data.products)).catch(() => setAll([])); }, []);
  const list = useMemo(() => {
    if (!all) return [];
    const t = q.trim().toLowerCase();
    if (t) return all.filter((p) => p.title.toLowerCase().includes(t)).slice(0, 20);
    return all.filter((p) => p.category === "Corredini")
      .sort((a, b) => (b.best_seller - a.best_seller) || ((b.beauty || 0) - (a.beauty || 0))).slice(0, 8);
  }, [all, q]);
  return (
    <div className="rounded-2xl bg-white border border-[#EAE3DC] p-5" data-testid="portal-products">
      <h2 className="font-medium text-[#2D2825]">Condividi un prodotto</h2>
      <p className="text-sm text-[#5C534E] mt-1">Ogni link porta al prodotto nell'app con il tuo codice: gli acquisti vengono contati per te.</p>
      <div className="relative mt-3">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B8ADA5]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nel catalogo (es. tutina, body, merino)" className="pl-9" data-testid="portal-search" />
      </div>
      <div className="mt-3 text-xs uppercase tracking-[0.18em] font-semibold text-[#4E7363]">{q.trim() ? "Risultati" : "Corredini nascita in evidenza"}</div>
      {!all ? <Loader2 className="animate-spin text-[#8C7F78] mt-3" size={18} /> : list.length === 0 ? (
        <p className="text-sm text-[#8C7F78] mt-3" data-testid="portal-no-results">Nessun prodotto trovato.</p>
      ) : <div className="divide-y divide-[#F1ECE7]">{list.map((p) => <ProductRow key={p.id} p={p} code={code} />)}</div>}
    </div>
  );
};

export default function InfluencerPortal() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/influencer-portal/${token}`).then((r) => setD(r.data)).catch((e) => setErr(e.response?.data?.detail || "Link non valido")); }, [token]);

  if (err) return <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center p-6 text-[#8C7F78]" data-testid="portal-error">{err}</div>;
  if (!d) return <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center"><Loader2 className="animate-spin text-[#8C7F78]" /></div>;
  const link = `${window.location.origin}/?ref=${d.code}`;
  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="influencer-portal">
      <header className="bg-white border-b border-[#EAE3DC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <img src={LOGO} alt="Minime Baby" className="h-9 w-auto object-contain" />
          <span className="text-xs text-[#8C7F78]">Area influencer</span>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-8 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C87D75] inline-flex items-center gap-1.5"><Sparkles size={13} /> Ciao {d.name.split(" ")[0]}</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#2D2825] mt-2">I tuoi risultati con Minime</h1>
          <p className="text-[#5C534E] mt-2 text-sm">Guadagni il {d.rate}% sul totale prodotti di ogni ordine fatto dall'app da chi arriva dal tuo link (entro 30 giorni dalla visita).</p>
        </div>
        {d.warning && <div className="text-xs text-[#B07A3B]">{d.warning}</div>}
        <Stats d={d} />
        <div className="rounded-2xl bg-white border border-[#EAE3DC] p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="portal-main-link">
          <div className="min-w-0">
            <div className="font-medium text-[#2D2825]">Il tuo link personale</div>
            <div className="text-sm text-[#4A7496] break-all" data-testid="portal-link">{link}</div>
          </div>
          <Share url={link} text="Ho scoperto Minime Baby, l'app per preparare il corredino del tuo bimbo in 100% cotone:" testid="portal-main" />
        </div>
        <Products code={d.code} />
        <Orders recent={d.recent} />
        <p className="text-xs text-[#8C7F78] text-center pb-6">Questa pagina è privata: non condividere questo indirizzo.</p>
      </main>
    </div>
  );
}
